/**
 * Puntuación de seguridad (gamificación NO punitiva) del vault.
 *
 * Zero-knowledge: esta lógica es PURA y opera sobre SEÑALES ya derivadas en el
 * cliente (fuerza 0–4, reutilización, antigüedad, presencia de 2FA). NUNCA
 * recibe contraseñas en claro ni ningún secreto — el llamador estima la fuerza
 * localmente (con `estimateStrength`) y pasa solo los indicadores. El servidor
 * jamás ve nada de esto: el score se calcula y se muestra en el dispositivo.
 *
 * Filosofía: la puntuación anima a mejorar la higiene (contraseñas fuertes,
 * únicas, recientes, con 2FA) SIN castigar. Un vault vacío no está "mal": el
 * score parte alto y las sugerencias son mejoras concretas y accionables,
 * ordenadas por el impacto potencial en puntos.
 */

/** Señal por item (SIN secretos): lo mínimo para puntuar la higiene. */
export interface SecuritySignal {
  readonly id: string;
  /** Tipo de item (login/note/card/identity). */
  readonly type: "login" | "note" | "card" | "identity";
  /** ¿El item tiene contraseña que puntuar? */
  readonly hasPassword: boolean;
  /** Fuerza estimada 0 (pésima) … 4 (excelente). Solo si `hasPassword`. */
  readonly strengthScore?: 0 | 1 | 2 | 3 | 4;
  /** La contraseña se repite en otro item del vault. */
  readonly reused?: boolean;
  /** Antigüedad de la última actualización, en días. */
  readonly ageDays?: number;
  /** El item tiene un segundo factor (semilla TOTP). */
  readonly hasTotp?: boolean;
}

/** Tipos de mejora que la gamificación sugiere (siempre en positivo). */
export type ImprovementKind = "strengthen" | "unique" | "refresh" | "twofactor";

export interface Improvement {
  readonly kind: ImprovementKind;
  /** Nº de items a los que aplica la mejora. */
  readonly count: number;
  /** Puntos aproximados que se ganarían al resolverla (para ordenar/priorizar). */
  readonly potential: number;
}

/** Desglose 0–100 por dimensión (para la UI del medidor). */
export interface SecurityBreakdown {
  readonly strength: number;
  readonly uniqueness: number;
  readonly freshness: number;
  readonly twoFactor: number;
}

export type SecurityLevel = "getting-started" | "building" | "solid" | "strong" | "fortified";

export interface SecurityScore {
  /** Puntuación global 0–100 (redondeada). */
  readonly score: number;
  readonly level: SecurityLevel;
  readonly breakdown: SecurityBreakdown;
  /** Nº de items con contraseña analizados (base de las dimensiones de contraseña). */
  readonly analyzed: number;
  /** Nº de logins (base de la cobertura de 2FA). */
  readonly logins: number;
  /** Mejoras sugeridas, mayor impacto primero. Vacío = higiene impecable. */
  readonly improvements: readonly Improvement[];
}

export interface SecurityScoreOptions {
  /** A partir de esta fuerza (inclusive) la contraseña se considera sana. Def. 3. */
  readonly healthyStrength?: number;
  /** Días desde los que una contraseña se considera antigua. Def. 365. */
  readonly maxAgeDays?: number;
}

/** Pesos de cada dimensión (suman 1). La fuerza y la unicidad pesan más. */
const WEIGHTS = { strength: 0.4, uniqueness: 0.25, freshness: 0.15, twoFactor: 0.2 } as const;

const EMPTY_BREAKDOWN: SecurityBreakdown = {
  strength: 100,
  uniqueness: 100,
  freshness: 100,
  twoFactor: 100,
};

function levelFor(score: number): SecurityLevel {
  if (score >= 95) {
    return "fortified";
  }
  if (score >= 85) {
    return "strong";
  }
  if (score >= 70) {
    return "solid";
  }
  if (score >= 50) {
    return "building";
  }
  return "getting-started";
}

/**
 * Calcula la puntuación de seguridad a partir de señales locales. Pura y
 * determinista. Un vault sin contraseñas puntúa 100 (nada que endurecer) — la
 * gamificación nunca penaliza por empezar.
 */
export function computeSecurityScore(
  signals: readonly SecuritySignal[],
  options: SecurityScoreOptions = {},
): SecurityScore {
  const { healthyStrength = 3, maxAgeDays = 365 } = options;

  const withPassword = signals.filter((s) => s.hasPassword);
  const logins = signals.filter((s) => s.type === "login");
  const analyzed = withPassword.length;

  // ── Dimensión: fuerza ──────────────────────────────────────────────────
  // Promedio normalizado (score/4). Sin contraseñas → 100 (sin superficie).
  const strength =
    analyzed === 0
      ? 100
      : (withPassword.reduce((sum, s) => sum + (s.strengthScore ?? 0), 0) / (analyzed * 4)) * 100;

  const weakCount = withPassword.filter((s) => (s.strengthScore ?? 0) < healthyStrength).length;

  // ── Dimensión: unicidad ────────────────────────────────────────────────
  const reusedCount = withPassword.filter((s) => s.reused === true).length;
  const uniqueness = analyzed === 0 ? 100 : ((analyzed - reusedCount) / analyzed) * 100;

  // ── Dimensión: frescura (antigüedad) ───────────────────────────────────
  const oldCount = withPassword.filter(
    (s) => s.ageDays !== undefined && s.ageDays > maxAgeDays,
  ).length;
  const freshness = analyzed === 0 ? 100 : ((analyzed - oldCount) / analyzed) * 100;

  // ── Dimensión: 2FA (cobertura sobre logins) ────────────────────────────
  const totpCount = logins.filter((s) => s.hasTotp === true).length;
  const missingTotp = logins.length - totpCount;
  const twoFactor = logins.length === 0 ? 100 : (totpCount / logins.length) * 100;

  const breakdown: SecurityBreakdown =
    signals.length === 0
      ? EMPTY_BREAKDOWN
      : {
          strength: Math.round(strength),
          uniqueness: Math.round(uniqueness),
          freshness: Math.round(freshness),
          twoFactor: Math.round(twoFactor),
        };

  const score = Math.round(
    strength * WEIGHTS.strength +
      uniqueness * WEIGHTS.uniqueness +
      freshness * WEIGHTS.freshness +
      twoFactor * WEIGHTS.twoFactor,
  );

  // ── Mejoras (positivas), ordenadas por impacto potencial en puntos ──────
  const candidates: Improvement[] = [];
  if (weakCount > 0 && analyzed > 0) {
    candidates.push({
      kind: "strengthen",
      count: weakCount,
      potential: Math.round((weakCount / analyzed) * WEIGHTS.strength * 100),
    });
  }
  if (reusedCount > 0 && analyzed > 0) {
    candidates.push({
      kind: "unique",
      count: reusedCount,
      potential: Math.round((reusedCount / analyzed) * WEIGHTS.uniqueness * 100),
    });
  }
  if (oldCount > 0 && analyzed > 0) {
    candidates.push({
      kind: "refresh",
      count: oldCount,
      potential: Math.round((oldCount / analyzed) * WEIGHTS.freshness * 100),
    });
  }
  if (missingTotp > 0 && logins.length > 0) {
    candidates.push({
      kind: "twofactor",
      count: missingTotp,
      potential: Math.round((missingTotp / logins.length) * WEIGHTS.twoFactor * 100),
    });
  }
  const improvements = candidates.toSorted((a, b) => b.potential - a.potential);

  return {
    score,
    level: levelFor(score),
    breakdown,
    analyzed,
    logins: logins.length,
    improvements,
  };
}

/** Item descifrado (SOLO en cliente) del que se derivan las señales de score. */
export interface ScorableItem {
  readonly id: string;
  readonly type: "login" | "note" | "card" | "identity";
  readonly password?: string;
  readonly totpSecret?: string;
  readonly updatedAt?: string;
}

/**
 * Convierte items descifrados (en cliente) en `SecuritySignal[]` SIN filtrar
 * secretos: la fuerza se estima con el `estimate` inyectado (local) y solo se
 * conserva el score numérico; la reutilización se detecta por igualdad de
 * contraseña, pero el resultado NO contiene ninguna contraseña. Pura y
 * determinista (con `now` fijo). Zero-knowledge: la salida es apta para métrica.
 */
export function buildSecuritySignals(
  items: readonly ScorableItem[],
  estimate: (password: string) => { score: 0 | 1 | 2 | 3 | 4 },
  now: number = Date.now(),
): SecuritySignal[] {
  // Cuenta de cada contraseña para marcar reutilización (nunca se emite).
  const counts = new Map<string, number>();
  for (const item of items) {
    if (item.password) {
      counts.set(item.password, (counts.get(item.password) ?? 0) + 1);
    }
  }
  const msPerDay = 24 * 60 * 60 * 1000;
  return items.map((item) => {
    const hasPassword = Boolean(item.password);
    const ageDays =
      item.updatedAt && !Number.isNaN(Date.parse(item.updatedAt))
        ? Math.max(0, Math.floor((now - Date.parse(item.updatedAt)) / msPerDay))
        : undefined;
    return {
      id: item.id,
      type: item.type,
      hasPassword,
      strengthScore: hasPassword ? estimate(item.password ?? "").score : undefined,
      reused: hasPassword ? (counts.get(item.password ?? "") ?? 0) > 1 : undefined,
      ageDays,
      hasTotp: Boolean(item.totpSecret),
    };
  });
}
